import { type FC, type ReactNode, useState } from 'react'
import { type SerializedError } from '@reduxjs/toolkit'
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { useLocation } from 'react-router-dom'
import { Spinner } from '../../../components/Spinner/Spinner'
import { NavigationSidebar } from '../../../components/NavigationWithSideBar/NavigationSidebar'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { useCanEditAccountScheduling } from '../../Scheduling/shared/useCanEditAccountScheduling'
import { useSchedulingTemplatesEnabled } from '../../Scheduling/shared/useSchedulingTemplatesEnabled'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'
import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useGetAccountSchedulingSettingsQuery, useUpdateAccountSchedulingSettingsMutation } from '../../../store/scheduling/scheduling'
import { type AccountSchedulingSettings } from '../../../models/Scheduling'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { SharedEventTypesSection } from './sections/SharedEventTypesSection'
import { PublicBookingPageSection } from './sections/PublicBookingPageSection'
import { UserControlsSection } from './sections/UserControlsSection'
import { AvailabilitySection } from './sections/AvailabilitySection'
import { MessageTemplatesSection } from './sections/MessageTemplatesSection'
import { RemindersSection } from './sections/RemindersSection'
import { type SmsChannelState } from './sections/ReminderSettingsCard'
import { TextMessagesSection } from './sections/TextMessagesSection'
import { SchedulingAdminBanner } from './SchedulingAdminBanner'

export type UpdateAccountSettings = (update: Partial<AccountSchedulingSettings>) => Promise<void>

type AccountSettingsGroup = keyof AccountSchedulingSettings

const pickGroups = (settings: AccountSchedulingSettings, groups: AccountSettingsGroup[]): Partial<AccountSchedulingSettings> =>
  groups.reduce<Partial<AccountSchedulingSettings>>((picked, group) => ({ ...picked, [group]: settings[group] }), {})

const dropGroups = (edits: Partial<AccountSchedulingSettings>, groups: AccountSettingsGroup[]): Partial<AccountSchedulingSettings> => {
  const remaining = { ...edits }
  groups.forEach((group) => delete remaining[group])
  return remaining
}

const SMS_QUOTA_ERROR_LOCATION = 'scheduling:smsNotifications'

interface LocatedApiErrorResponse {
  errors?: { location?: string }[]
}

const isSmsQuotaError = (error: FetchBaseQueryError | SerializedError | undefined): boolean =>
  error != null &&
  'data' in error &&
  ((error.data as LocatedApiErrorResponse | undefined)?.errors ?? []).some((apiError) => apiError.location === SMS_QUOTA_ERROR_LOCATION)

const BASE_NAV_SECTIONS = [
  { id: 'event-types', label: 'Shared event types' },
  { id: 'booking-page', label: 'Public booking pages' },
  { id: 'user-controls', label: 'User controls' },
  { id: 'availability', label: 'Availability' },
]

const REMINDERS_NAV_SECTION = { id: 'reminders', label: 'Reminders' }

const TEXT_MESSAGES_NAV_SECTION = { id: 'text-messages', label: 'Text messages' }

interface NavSectionsOptions {
  showReminders: boolean
  showSms: boolean
  templatesEnabled: boolean
}

const templatesLabel = (showSms: boolean, templatesEnabled: boolean) => (showSms && !templatesEnabled ? 'Message templates' : 'Email templates')

const buildNavSections = ({ showReminders, showSms, templatesEnabled }: NavSectionsOptions) => [
  ...BASE_NAV_SECTIONS,
  { id: 'message-templates', label: templatesLabel(showSms, templatesEnabled) },
  ...(showReminders ? [REMINDERS_NAV_SECTION] : []),
  ...(showSms ? [TEXT_MESSAGES_NAV_SECTION] : []),
]

const smsChannelState = (showSms: boolean, settings: AccountSchedulingSettings): SmsChannelState => {
  if (!showSms) {
    return 'hidden'
  }
  return settings.notifications.sms.enabled && settings.notifications.sms.reminders ? 'on' : 'off'
}

export const SchedulingSetupTab: FC = () => {
  const { isFetching: quotasFetching, hasQuota, quotaSuccess } = useZocksQuota()

  if (quotasFetching && !quotaSuccess) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  if (!quotaSuccess) {
    return null
  }

  if (!hasQuota('scheduling.enabled.value')) {
    return <Upsell feature={DELTA_TRIAL_FEATURES['scheduling']} />
  }

  return <SchedulingSetupTabView />
}

const SchedulingSetupTabView: FC = () => {
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const canEditScheduling = useCanEditAccountScheduling()
  const { hasQuota } = useZocksQuota()
  const showReminders = hasQuota('scheduling.reminders.value')
  const showSms = hasQuota('scheduling.smsNotifications.value')
  const { isEnabled: templatesEnabled } = useSchedulingTemplatesEnabled()
  const navSections = buildNavSections({ showReminders, showSms, templatesEnabled })
  const { accountId, id: userId } = useAppSelector(selectSafeAuthenticatedUser)
  const readOnly = !canEditScheduling
  const { data, isLoading } = useGetAccountSchedulingSettingsQuery({ accountId })
  const [updateAccountSettings, { isLoading: isUpdating }] = useUpdateAccountSchedulingSettingsMutation()
  const location = useLocation()
  const requestedSection = (location.state as { section?: string } | null)?.section
  const isRequestedSectionAvailable = requestedSection != null && navSections.some((section) => section.id === requestedSection)
  const [activeSectionId, setActiveSectionId] = useState(isRequestedSectionAvailable ? requestedSection : 'event-types')
  const [handledLocationKey, setHandledLocationKey] = useState(location.key)
  if (location.key !== handledLocationKey) {
    setHandledLocationKey(location.key)
    if (isRequestedSectionAvailable) {
      setActiveSectionId(requestedSection)
    }
  }
  const [pendingEdits, setPendingEdits] = useState<Partial<AccountSchedulingSettings>>({})

  if (isLoading || !data) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  const settings = { ...data, ...pendingEdits }

  const handleUpdate: UpdateAccountSettings = async (update) => {
    const editedGroups = Object.keys(update) as AccountSettingsGroup[]
    setPendingEdits((current) => ({ ...current, ...update }))
    const result = await updateAccountSettings({ accountId, update })
    if ('error' in result) {
      setPendingEdits((current) => dropGroups(current, editedGroups))
      dispatch(
        addToast(
          isSmsQuotaError(result.error)
            ? { variant: 'error', title: "Text messages aren't available", messages: ["Your plan doesn't include text messages."] }
            : { variant: 'error', title: 'Could not save the setting', messages: ['Please try again.'] },
        ),
      )
    } else {
      setPendingEdits((current) => ({ ...current, ...pickGroups(result.data, editedGroups) }))
      trackingClient.track({ eventName: TrackEvent.SCHEDULING_ACCOUNT_SETTINGS_UPDATED, eventDetails: { fields: editedGroups } })
    }
  }

  const sectionContent: Record<string, ReactNode> = {
    'event-types': <SharedEventTypesSection readOnly={readOnly} accountBookingEnabled={Boolean(settings.publicBookingPage.accountBookingEnabled)} />,
    'booking-page': <PublicBookingPageSection settings={settings} readOnly={readOnly} isSaving={isUpdating} onUpdate={handleUpdate} />,
    'user-controls': <UserControlsSection settings={settings} readOnly={readOnly} isSaving={isUpdating} onUpdate={handleUpdate} />,
    availability: <AvailabilitySection settings={settings} readOnly={readOnly} isSaving={isUpdating} onUpdate={handleUpdate} />,
    'message-templates': <MessageTemplatesSection onShowReminders={showReminders ? () => setActiveSectionId('reminders') : undefined} />,
    reminders: showReminders ? (
      <RemindersSection
        readOnly={readOnly}
        smsChannel={smsChannelState(showSms, settings)}
        templatesLabel={templatesEnabled ? undefined : templatesLabel(showSms, templatesEnabled)}
        onShowMessageTemplates={() => setActiveSectionId('message-templates')}
        onShowTextMessages={() => setActiveSectionId('text-messages')}
      />
    ) : null,
    'text-messages': showSms ? (
      <TextMessagesSection
        settings={settings}
        readOnly={readOnly}
        isSaving={isUpdating}
        showReminderStatus={showReminders}
        showTextPreviews={templatesEnabled}
        onUpdate={handleUpdate}
        onShowReminders={() => setActiveSectionId('reminders')}
        onShowMessageTemplates={() => setActiveSectionId('message-templates')}
      />
    ) : null,
  }

  return (
    <div className='d-flex flex-column gap-4'>
      {readOnly ? (
        <div className='paragraph-small zocks-text-gray-600 p-3 border rounded zocks-border-color-gray-200 zocks-bg-color-gray-100'>
          <i className='bi bi-info-circle me-1' />
          These Scheduling settings are managed by your account admins and are view-only for you.
        </div>
      ) : (
        <SchedulingAdminBanner userId={userId} />
      )}
      <div className='scroll-navigation__container'>
        <div className='d-flex flex-column gap-4'>
          <NavigationSidebar sections={navSections} activeSectionId={activeSectionId} onSectionClick={setActiveSectionId} />
        </div>
        <div className='scroll-navigation__sections'>{sectionContent[activeSectionId]}</div>
      </div>
    </div>
  )
}
